import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { ProductModal } from "@/components/ProductModal";
import { StockAlertForm } from "@/components/StockAlertForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const gbp = (pence) => `£${(pence / 100).toFixed(2)}`;

const TINTS = {
  original_24: "#DDFBFF",
  butter_chicken_24: "#FFE5CC",
  tamarind_24: "#EEDDFF",
  mixed_case_24: "#FFF4CC",
  snack_pack: "#FFD1E8",
  mango_dip: "#FFF4CC",
  bombay_mix_pot: "#FFD1E8",
  dip_deal: "#FFD1E8",
};

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "proppadoms", label: "Proppadoms" },
  { key: "bundles", label: "Bundles" },
  { key: "dips", label: "Dips & Pots" },
  { key: "merch", label: "Merch" },
];

const categoryOf = (key) => {
  if (["original_24", "butter_chicken_24", "tamarind_24", "mixed_case_24"].includes(key)) return "proppadoms";
  if (["mango_dip", "bombay_mix_pot"].includes(key)) return "dips";
  if (key.startsWith("tee_")) return "merch";
  return "bundles";
};

const ProductCard = ({ product, index, onInfo }) => {
  const { items, addItem, setQuantity } = useCart();
  const soldOut = product.stock !== null && product.stock !== undefined && product.stock <= 0;
  const comingSoon = !!product.coming_soon;
  const lowStock = !soldOut && product.stock !== null && product.stock !== undefined && product.stock <= 5;
  const tint = TINTS[product.lookup_key] || "#F3F0FF";
  const inCart = items.find((i) => i.lookup_key === product.lookup_key)?.quantity || 0;
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.5, delay: (index % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className={`group ${index === 0 ? "md:col-span-2" : ""}`}
      data-testid={`product-card-${product.lookup_key}`}
    >
      <div
        onClick={() => product.nutrition && onInfo(product)}
        className={`relative rounded-[28px] transition-transform duration-300 group-hover:-translate-y-1.5 ${
          index === 0 ? "h-80 md:h-[24rem]" : "h-64"
        } ${product.nutrition ? "cursor-pointer" : ""}`}
        style={{ backgroundColor: tint }}
      >
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className={`w-full h-full object-contain p-6 mix-blend-multiply transition-transform duration-500 ease-out group-hover:scale-105 group-hover:-rotate-2 ${
            soldOut ? "opacity-40 grayscale" : ""
          }`}
          data-testid={`product-image-${product.lookup_key}`}
        />
        {soldOut && (
          <span
            className="absolute top-4 left-4 bg-[#FF007A] text-white rounded-full px-4 py-1.5 text-[11px] font-bold uppercase tracking-wide"
            data-testid={`sold-out-badge-${product.lookup_key}`}
          >
            Sold out
          </span>
        )}
        {comingSoon && (
          <span
            className="absolute top-4 right-4 bg-[#FFD600] text-[#2B0B3C] rounded-full px-4 py-1.5 text-[11px] font-bold uppercase tracking-wide"
            data-testid={`coming-soon-badge-${product.lookup_key}`}
          >
            Coming soon
          </span>
        )}
        {lowStock && (
          <span
            className="absolute top-4 left-4 bg-[#FFD600] text-[#2B0B3C] rounded-full px-4 py-1.5 text-[11px] font-bold uppercase tracking-wide"
            data-testid={`low-stock-badge-${product.lookup_key}`}
          >
            Only {product.stock} left
          </span>
        )}
      </div>
      <div className="pt-5 px-1 flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#4A148C] mb-1">{product.tag}</p>
          <h3 className="font-display text-2xl font-bold tracking-tight text-[#2B0B3C]" data-testid={`product-name-${product.lookup_key}`}>
            {product.name}
          </h3>
          <p className="text-sm font-medium text-[#2B0B3C]/60 mt-1 max-w-xs">{product.strap}</p>
          {product.nutrition && (
            <button
              onClick={() => onInfo(product)}
              data-testid={`info-link-${product.lookup_key}`}
              className="mt-2 text-[11px] font-bold uppercase tracking-[0.15em] text-[#4A148C] underline underline-offset-4 hover:text-[#FF6B00] transition-colors duration-200"
            >
              Ingredients &amp; nutrition
            </button>
          )}
          <p className="mt-2 font-display text-xl font-bold text-[#2B0B3C]" data-testid={`product-price-${product.lookup_key}`}>
            {gbp(product.price_pence)}
          </p>
          {(soldOut || comingSoon) && <StockAlertForm lookupKey={product.lookup_key} />}
        </div>
        {soldOut ? null : inCart > 0 && !comingSoon ? (
          <div
            className="shrink-0 mt-1 inline-flex items-center bg-[#2B0B3C] text-white rounded-full"
            data-testid={`card-stepper-${product.lookup_key}`}
          >
            <button
              onClick={() => setQuantity(product.lookup_key, inCart - 1)}
              aria-label={`Remove one ${product.name}`}
              data-testid={`card-decrease-${product.lookup_key}`}
              className="w-11 h-11 flex items-center justify-center rounded-full hover:text-[#FFD600] transition-colors duration-200"
            >
              <Minus size={16} strokeWidth={3} />
            </button>
            <span className="min-w-7 text-center font-display font-bold" data-testid={`card-qty-${product.lookup_key}`}>
              {inCart}
            </span>
            <button
              onClick={() => {
                addItem(product, false);
              }}
              aria-label={`Add one more ${product.name}`}
              data-testid={`add-to-cart-${product.lookup_key}`}
              className="w-11 h-11 flex items-center justify-center rounded-full hover:text-[#FFD600] transition-colors duration-200"
            >
              <Plus size={16} strokeWidth={3} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => {
              addItem(product);
              toast(`${product.name} added to your basket`);
            }}
            disabled={soldOut || comingSoon}
            data-testid={`add-to-cart-${product.lookup_key}`}
            aria-label={soldOut ? `${product.name} is sold out` : comingSoon ? `${product.name} is coming soon` : `Add ${product.name} to basket`}
            className={`shrink-0 w-12 h-12 rounded-full flex items-center justify-center transition-[transform,background-color] duration-200 mt-1 ${
              soldOut || comingSoon
                ? "bg-[#2B0B3C]/10 text-[#2B0B3C]/30 cursor-not-allowed"
                : "bg-[#2B0B3C] text-white hover:bg-[#FF6B00]"
            }`}
          >
            <Plus size={18} strokeWidth={3} />
          </button>
        )}
      </div>
    </motion.article>
  );
};

export const Products = () => {
  const [products, setProducts] = useState([]);
  const [error, setError] = useState(false);
  const [category, setCategory] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((res) => setProducts(res.data))
      .catch(() => setError(true));
  }, []);

  const visible = category === "all" ? products : products.filter((p) => categoryOf(p.lookup_key) === category);

  return (
    <section id="range" className="py-24 lg:py-32" data-testid="products-section">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-10 flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <p className="inline-block bg-white border border-[#2B0B3C]/15 rounded-full px-5 py-2 text-xs font-bold tracking-[0.15em] uppercase text-[#2B0B3C]/70 mb-6">
              The Range
            </p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight leading-[0.95] text-[#2B0B3C]">
              Everything for a<br />
              <span className="text-[#FF6B00]">proppa</span> night in.
            </h2>
          </div>
          <p className="max-w-xs text-sm font-medium leading-relaxed text-[#2B0B3C]/60">
            Everything is packed to order and shipped anywhere in the UK. Trade pricing available below.
          </p>
        </motion.div>

        <div className="flex flex-wrap gap-2.5 mb-14" data-testid="category-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => setCategory(c.key)}
              data-testid={`filter-${c.key}`}
              className={`rounded-full px-6 py-2.5 text-sm font-bold uppercase tracking-wide transition-[background-color,color] duration-200 ${
                category === c.key ? "bg-[#2B0B3C] text-white" : "bg-white text-[#2B0B3C] hover:bg-[#FFD600]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {error && (
          <p className="text-sm font-bold text-[#FF007A]" data-testid="products-error">
            The range couldn't load right now — please refresh.
          </p>
        )}

        <motion.div layout className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-14">
          <AnimatePresence mode="popLayout">
            {visible.map((p, i) => (
              <ProductCard key={p.lookup_key} product={p} index={i} onInfo={setSelected} />
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
      <ProductModal
        product={selected}
        tint={selected ? TINTS[selected.lookup_key] || "#F3F0FF" : "#F3F0FF"}
        open={!!selected}
        onClose={() => setSelected(null)}
      />
    </section>
  );
};
